import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  Stack,
  InputAdornment,
  Chip,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  PictureAsPdf as PdfIcon,
  Send as SendIcon,
  Paid as PaidIcon,
} from "@mui/icons-material";
import { usePDF } from 'react-to-pdf';
import { RootState, AppDispatch } from "../../store";
import {
  fetchBills,
  createBill,
  updateBill,
  deleteBill,
  sendBillAction,
  markBillAsPaidAction,
  searchBills,
} from "../../store/slices/billingSlice";
import {
  Bill,
  BillCreateInput,
  BillUpdateInput,
} from "../../services/billingService";
import { getFirmSettings } from "../../store/slices/settingsSlice";
import { useConfirm } from "../../contexts/ConfirmDialogContext";

const Bills: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const confirm = useConfirm();
  const { bills, loading, error } = useSelector(
    (state: RootState) => state.bills,
  );
  const { user } = useSelector((state: RootState) => state.auth);
  const { firm } = useSelector((state: RootState) => state.settings);

  const { toPDF, targetRef } = usePDF({ filename: 'bill.pdf' });
  const billContentRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBill, setEditingBill] = useState<Bill | null>(null);
  const [formData, setFormData] = useState<BillCreateInput>({
    customer_id: 0,
    case_id: 0,
    items: [{ description: "", quantity: 1, unit_price: 0, total: 0 }],
    subtotal: 0,
    tax_rate: 0,
    tax_amount: 0,
    discount: 0,
    total: 0,
    due_date: "",
    issued_date: new Date().toISOString().split("T")[0],
    currency: "USD",
  });
  const [viewingBill, setViewingBill] = useState<Bill | null>(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);

  // Update the targetRef to point to our bill content
  React.useEffect(() => {
    if (billContentRef.current && viewingBill) {
      targetRef.current = billContentRef.current;
    }
  }, [viewingBill, targetRef]);

  const openViewBillDialog = (bill: Bill) => {
    setViewingBill(bill);
    setOpenViewDialog(true);
  };

  useEffect(() => {
    if (user?.user_id) {
      dispatch(fetchBills(user.user_id.toString()));
      dispatch(getFirmSettings());
    }
  }, [dispatch, user]);

  const handleSearch = () => {
    if (user?.user_id && searchQuery.trim()) {
      dispatch(
        searchBills({ userId: user.user_id.toString(), query: searchQuery }),
      );
    } else if (user?.user_id) {
      dispatch(fetchBills(user.user_id.toString()));
    }
  };

  const handleCreateBill = async () => {
    try {
      const result = await dispatch(createBill(formData));
      if ("error" in result) {
        console.error("Failed to create bill:", result.error);
      } else {
        setOpenDialog(false);
        resetForm();
      }
    } catch (error) {
      console.error("Failed to create bill:", error);
    }
  };

  const handleUpdateBill = async () => {
    if (!editingBill) return;

    const updateData: BillUpdateInput = {
      id: editingBill.id,
      customer_id: formData.customer_id,
      case_id: formData.case_id,
      items: formData.items,
      subtotal: formData.subtotal,
      tax_rate: formData.tax_rate,
      tax_amount: formData.tax_amount,
      discount: formData.discount,
      total: formData.total,
      due_date: formData.due_date,
      issued_date: formData.issued_date,
      currency: formData.currency,
    };

    try {
      const result = await dispatch(updateBill(updateData));
      if ("error" in result) {
        console.error("Failed to update bill:", result.error);
      } else {
        setOpenDialog(false);
        setEditingBill(null);
        resetForm();
      }
    } catch (error) {
      console.error("Failed to update bill:", error);
    }
  };

  const handleDeleteBill = async (billId: number) => {
    if (!user?.user_id) return;

    const ok = await confirm(t("billing.confirmDeleteInvoice"));
    if (ok) {
      try {
        const result = await dispatch(
          deleteBill({ billId, userId: user.user_id.toString() }),
        );
        if ("error" in result) {
          console.error("Failed to delete bill:", result.error);
        }
      } catch (error) {
        console.error("Failed to delete bill:", error);
      }
    }
  };

  const handleSendBill = async (billId: number) => {
    try {
      const result = await dispatch(sendBillAction(billId));
      if ("error" in result) {
        console.error("Failed to send bill:", result.error);
      }
    } catch (error) {
      console.error("Failed to send bill:", error);
    }
  };

  const handleMarkAsPaid = async (billId: number) => {
    try {
      const result = await dispatch(
        markBillAsPaidAction({
          billId,
          paymentMethod: "bank_transfer",
          paymentDate: new Date().toISOString().split("T")[0],
        }),
      );
      if ("error" in result) {
        console.error("Failed to mark bill as paid:", result.error);
      }
      dispatch(fetchBills(user!.user_id.toString()));
    } catch (error) {
      console.error("Failed to mark bill as paid:", error);
    }
  };

  // Helper function to get firm setting value from settings array
  const getFirmSetting = (key: string): string => {
    if (!firm?.settings) return "";
    const setting = firm.settings.find((s: any) => s.key === key);
    return setting?.value || "";
  };

  const handlePrintBill = () => {
    if (!viewingBill || !firm) return;
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert(t('billing.allowPopupsToPrint'));
      return;
    }
    
    // Get firm settings
    const bankDetails = getFirmSetting('bank_details');
    const billFooter = getFirmSetting('bill_footer');
    
    // Create the print document with proper layout
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${t('billing.billTitleWithNumber', { number: viewingBill.bill_number })}</title>
          <style>
            @media print {
              body { 
                margin: 15px; 
                font-size: 10px;
                line-height: 1.2;
              }
              .no-print { display: none !important; }
              .page-break { page-break-after: always; }
            }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 15px;
              color: #333;
              font-size: 12px;
            }
            .container {
              max-width: 100%;
              margin: 0 auto;
            }
            .firm-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 20px;
              border-bottom: 2px solid #1976d2;
              padding-bottom: 15px;
              page-break-inside: avoid;
            }
            .firm-info {
              flex: 1;
            }
            .firm-info img {
              max-width: 240px;
              max-height: 120px;
              margin-bottom: 8px;
            }
            .firm-name {
              color: #1976d2;
              font-size: 18px;
              font-weight: 700;
              margin: 0 0 4px 0;
            }
            .firm-address, .firm-contact {
              font-size: 10px;
              color: #666;
              margin: 2px 0;
            }
            .bill-info {
              text-align: right;
            }
            .bill-label {
              color: #1976d2;
              font-size: 24px;
              font-weight: 700;
              margin: 0;
            }
            .bill-number {
              font-size: 16px;
              font-weight: 500;
              margin: 4px 0;
            }
            .bill-date {
              font-size: 10px;
              color: #666;
              margin: 0;
            }
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px;
              margin-bottom: 20px;
              page-break-inside: avoid;
            }
            .info-section {
              margin-bottom: 8px;
            }
            .info-label {
              font-size: 10px;
              color: #666;
              margin-bottom: 2px;
              text-transform: uppercase;
              font-weight: 500;
            }
            .info-value {
              font-weight: 500;
              font-size: 11px;
            }
            .section {
              margin-bottom: 25px;
              page-break-inside: avoid;
            }
            .section-title {
              color: #1976d2;
              font-size: 14px;
              font-weight: 700;
              margin: 0 0 10px 0;
              padding-bottom: 5px;
              border-bottom: 2px solid #1976d2;
              text-transform: uppercase;
            }
            .table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 10px;
              font-size: 9px;
            }
            .table th {
              background-color: #f5f5f5;
              padding: 8px 6px;
              text-align: left;
              font-weight: 600;
              border: 1px solid #ddd;
              font-size: 9px;
            }
            .table td {
              padding: 6px;
              border: 1px solid #ddd;
              font-size: 9px;
            }
            .table td.text-right {
              text-align: right;
            }
            .table td.text-center {
              text-align: center;
            }
            .table .total-row {
              background-color: #ff9800 !important;
              color: white !important;
              font-weight: 600;
            }
            .table .description-total-row {
              background-color: #9c27b0 !important;
              color: white !important;
              font-weight: 600;
            }
            .totals {
              text-align: right;
              margin: 15px 0;
              page-break-inside: avoid;
            }
            .totals .total-line {
              display: flex;
              justify-content: space-between;
              margin: 4px 0;
              font-size: 10px;
            }
            .totals .total-line .label {
              margin-right: 20px;
            }
            .totals .grand-total {
              border-top: 2px solid #ddd;
              padding-top: 4px;
              font-weight: 700;
              font-size: 12px;
            }
            .bank-details {
              margin: 20px 0;
              page-break-inside: avoid;
            }
            .bank-details p {
              white-space: pre-line;
              font-size: 9px;
              margin: 0;
              line-height: 1.3;
            }
            .notes {
              margin: 15px 0;
              page-break-inside: avoid;
            }
            .notes p {
              font-size: 9px;
              margin: 0;
              line-height: 1.3;
            }
            .footer {
              text-align: center;
              margin-top: 30px;
              padding-top: 15px;
              border-top: 1px solid #ddd;
              color: #666;
              font-size: 8px;
              font-style: italic;
              page-break-inside: avoid;
            }
            .footer p {
              margin: 2px 0;
              white-space: pre-line;
              line-height: 1.2;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <!-- Firm Header -->
            <div class="firm-header">
              <div class="firm-info">
                ${firm.logo_url ? `<img src="${firm.logo_url}" alt="Firm Logo" />` : ''}
                <h2 class="firm-name">${firm.name || t('billing.legalServices')}</h2>
                <p class="firm-address">${firm.address || ''}, ${firm.city || ''}, ${firm.country || ''}</p>
                <p class="firm-contact">${firm.email || ''} | ${firm.phone || ''}</p>
              </div>
              <div class="bill-info">
                <h1 class="bill-label">${t('billing.bill').toUpperCase()}</h1>
                <h2 class="bill-number">#${viewingBill.bill_number}</h2>
                <p class="bill-date">${viewingBill.issued_date}</p>
              </div>
            </div>

            <!-- Client and Case Info -->
            <div class="info-grid">
              <div>
                <div class="info-section">
                  <div class="info-label">${t('billing.client')}</div>
                  <div class="info-value">${viewingBill.customer?.first_name} ${viewingBill.customer?.last_name}</div>
                  ${viewingBill.customer?.company ? `<div class="info-value">${viewingBill.customer.company}</div>` : ''}
                </div>
                <div class="info-section">
                  <div class="info-label">${t('billing.issueDate')}</div>
                  <div class="info-value">${viewingBill.issued_date}</div>
                </div>
                <div class="info-section">
                  <div class="info-label">${t('billing.status')}</div>
                  <div class="info-value">${viewingBill.status.toUpperCase()}</div>
                </div>
              </div>
              <div>
                <div class="info-section">
                  <div class="info-label">${t('billing.case')}</div>
                  <div class="info-value">${viewingBill.case_file?.subject || '—'}</div>
                  ${viewingBill.case_file?.type ? `<div class="info-value">${viewingBill.case_file.type}</div>` : ''}
                </div>
                <div class="info-section">
                  <div class="info-label">${t('billing.dueDate')}</div>
                  <div class="info-value">${viewingBill.due_date}</div>
                </div>
                <div class="info-section">
                  <div class="info-label">${t('billing.currency')}</div>
                  <div class="info-value">${viewingBill.currency}</div>
                </div>
              </div>
            </div>

            <!-- Summary Section -->
            <div class="section">
              <h3 class="section-title">${t('billing.summary')}</h3>
              <table class="table">
                <thead>
                  <tr>
                    <th>${t('common.position')}</th>
                    <th>${t('billing.names')}</th>
                    <th class="text-right">${t('billing.rate')}</th>
                    <th class="text-right">${t('billing.hours')}</th>
                    <th class="text-right">${t('common.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  ${(typeof viewingBill.items === "string" ? JSON.parse(viewingBill.items) : viewingBill.items).map((item: any, index: number) => `
                    <tr>
                      <td>${item.position || t('billing.service')}</td>
                      <td>${item.description}</td>
                      <td class="text-right">${Number(item.unit_price).toLocaleString()}</td>
                      <td class="text-right">${item.quantity}</td>
                      <td class="text-right">${Number(item.total).toLocaleString()}</td>
                    </tr>
                  `).join('')}
                  <tr class="total-row">
                    <td colspan="3">${t('billing.totalChargeableAmount')}</td>
                    <td class="text-center">${(typeof viewingBill.items === "string" ? JSON.parse(viewingBill.items) : viewingBill.items).reduce((sum: number, item: any) => sum + Number(item.quantity), 0)}</td>
                    <td class="text-right">${Number(viewingBill.total).toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Description Section -->
            <div class="section">
              <h3 class="section-title">${t('common.description')}</h3>
              <table class="table">
                <thead>
                  <tr>
                    <th>${t('common.date')}</th>
                    <th>${t('common.description')}</th>
                    <th class="text-center">${t('billing.hours')}</th>
                    <th class="text-right">${t('common.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  ${(typeof viewingBill.items === "string" ? JSON.parse(viewingBill.items) : viewingBill.items).map((item: any, index: number) => `
                    <tr>
                      <td style="color: #666;">${viewingBill.issued_date}</td>
                      <td>${item.description}</td>
                      <td class="text-center">${item.quantity}</td>
                      <td class="text-right">${Number(item.total).toLocaleString()}</td>
                    </tr>
                  `).join('')}
                  <tr class="description-total-row">
                    <td colspan="2">${t('billing.totalHoursWorked')}</td>
                    <td class="text-center">${(typeof viewingBill.items === "string" ? JSON.parse(viewingBill.items) : viewingBill.items).reduce((sum: number, item: any) => sum + Number(item.quantity), 0)}</td>
                    <td class="text-right">${Number(viewingBill.total).toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Totals -->
            <div class="totals">
              <div class="total-line">
                <span class="label">${t('billing.subtotal')}:</span>
                <span>${Number(viewingBill.subtotal).toLocaleString()} ${viewingBill.currency}</span>
              </div>
              ${Number(viewingBill.discount) > 0 ? `
                <div class="total-line" style="color: #d32f2f;">
                  <span class="label">${t('billing.discount')}:</span>
                  <span>-${Number(viewingBill.discount).toLocaleString()} ${viewingBill.currency}</span>
                </div>
              ` : ''}
              ${Number(viewingBill.tax_amount) > 0 ? `
                <div class="total-line">
                  <span class="label">${t('billing.taxWithRate', { rate: viewingBill.tax_rate })}:</span>
                  <span>${Number(viewingBill.tax_amount).toLocaleString()} ${viewingBill.currency}</span>
                </div>
              ` : ''}
              <div class="total-line grand-total">
                <span class="label">${t('common.total')}:</span>
                <span>${Number(viewingBill.total).toLocaleString()} ${viewingBill.currency}</span>
              </div>
            </div>

            <!-- Bank Details -->
            ${bankDetails ? `
              <div class="bank-details">
                <h3 class="section-title">${t('billing.bankDetails')}</h3>
                <p>${bankDetails}</p>
              </div>
            ` : ''}

            <!-- Notes -->
            ${viewingBill.notes ? `
              <div class="notes">
                <h3 class="section-title">${t('common.notes')}</h3>
                <p>${viewingBill.notes}</p>
              </div>
            ` : ''}

            <!-- Footer -->
            ${billFooter ? `
              <div class="footer">
                <p>${billFooter}</p>
              </div>
            ` : ''}
          </div>
          <script>
            window.onload = function() {
              setTimeout(() => {
                window.print();
                window.close();
              }, 500);
            }
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  const handleDownloadPdf = async (bill: Bill) => {
    try {
      console.log('PDF download started for bill:', bill.bill_number);
      
      // Import jsPDF and necessary utilities
      const { jsPDF } = await import('jspdf');
      
      // Create a new PDF document
      const doc = new jsPDF();
      
      // Set up page with reduced margins
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, 210, 297, 'F'); // White background
      
      let yPosition = 15; // Reduced top margin
      
      // Firm Header Section with Logo Support
      if (firm?.logo_url) {
        try {
          // Try to load and add the firm logo using canvas method
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new Image();
          img.crossOrigin = 'anonymous';
          
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = firm.logo_url || '';
          });
          
          // Set canvas size
          canvas.width = img.width;
          canvas.height = img.height;
          
          // Draw image to canvas
          ctx?.drawImage(img, 0, 0);
          
          // Get canvas data URL
          const dataUrl = canvas.toDataURL('image/png');
          
          // Add logo to PDF
          const logoWidth = 40;
          const logoHeight = 20;
          doc.addImage(dataUrl, 'PNG', 20, yPosition, logoWidth, logoHeight);
          
          // Add firm name next to logo
          doc.setTextColor(25, 118, 210);
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.text(firm.name || t('billing.legalServices'), 70, yPosition + 10);
          yPosition += 25;
          
        } catch (error) {
          console.log('Could not load logo, using text fallback:', error);
          // Fallback to text if logo fails
          doc.setTextColor(25, 118, 210);
          doc.setFontSize(18);
          doc.setFont('helvetica', 'bold');
          doc.text(firm?.name || t('billing.legalServices'), 20, yPosition);
          yPosition += 12;
        }
      } else {
        // Add firm name as title if no logo
        doc.setTextColor(25, 118, 210);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text(firm?.name || 'Legal Services', 20, yPosition);
        yPosition += 12;
      }
      
      // Add firm contact info
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const firmInfo = `${firm?.address || ''}, ${firm?.city || ''}, ${firm?.country || ''}`;
      doc.text(firmInfo, 20, yPosition);
      yPosition += 6;
      const contactInfo = `${firm?.email || ''} | ${firm?.phone || ''}`;
      doc.text(contactInfo, 20, yPosition);
      yPosition += 10;
      
      // Bill Number (right aligned)
      doc.setTextColor(25, 118, 210);
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text(t('billing.bill').toUpperCase(), 190, yPosition - 20, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.text(`#${bill.bill_number}`, 190, yPosition - 12, { align: 'right' });
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(9);
      doc.text(bill.issued_date, 190, yPosition - 5, { align: 'right' });
      
      // Add horizontal line
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(20, yPosition, 190, yPosition);
      yPosition += 10;
      
      // Client and Case Info Grid (2 columns)
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      // Left column
      doc.setFont('helvetica', 'bold');
      doc.text(`${t('billing.client')}:`, 20, yPosition);
      yPosition += 6;
      doc.setFont('helvetica', 'normal');
      doc.text(`${bill.customer?.first_name} ${bill.customer?.last_name}`, 20, yPosition);
      yPosition += 6;
      if (bill.customer?.company) {
        doc.text(bill.customer.company, 20, yPosition);
        yPosition += 6;
      }
      
      doc.setFont('helvetica', 'bold');
      doc.text(`${t('billing.issueDate')}:`, 20, yPosition);
      yPosition += 6;
      doc.setFont('helvetica', 'normal');
      doc.text(bill.issued_date, 20, yPosition);
      yPosition += 6;
      
      doc.setFont('helvetica', 'bold');
      doc.text(`${t('billing.status')}:`, 20, yPosition);
      yPosition += 6;
      doc.setFont('helvetica', 'normal');
      doc.text(bill.status.toUpperCase(), 20, yPosition);
      yPosition += 10;
      
      // Right column
      let rightYPosition = yPosition - 42; // Start at same height as left column
      
      doc.setFont('helvetica', 'bold');
      doc.text(`${t('billing.case')}:`, 110, rightYPosition);
      rightYPosition += 6;
      doc.setFont('helvetica', 'normal');
      doc.text(bill.case_file?.subject || '—', 110, rightYPosition);
      rightYPosition += 6;
      if (bill.case_file?.type) {
        doc.text(bill.case_file.type, 110, rightYPosition);
        rightYPosition += 6;
      }
      
      doc.setFont('helvetica', 'bold');
      doc.text(`${t('billing.dueDate')}:`, 110, rightYPosition);
      rightYPosition += 6;
      doc.setFont('helvetica', 'normal');
      doc.text(bill.due_date, 110, rightYPosition);
      rightYPosition += 6;
      
      doc.setFont('helvetica', 'bold');
      doc.text(`${t('billing.currency')}:`, 110, rightYPosition);
      rightYPosition += 6;
      doc.setFont('helvetica', 'normal');
      doc.text(bill.currency, 110, rightYPosition);
      
      yPosition = Math.max(yPosition, rightYPosition) + 10;
      
      // Add horizontal line
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(20, yPosition, 190, yPosition);
      yPosition += 15;
      
      // Summary Section
      doc.setTextColor(25, 118, 210);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(t('billing.summary'), 20, yPosition);
      yPosition += 10;

      // Summary table header
      doc.setFillColor(245, 245, 245);
      doc.rect(20, yPosition, 170, 8, 'F');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(t('common.position'), 25, yPosition + 5);
      doc.text(t('billing.names'), 65, yPosition + 5);
      doc.text(t('billing.rate'), 110, yPosition + 5, { align: 'right' });
      doc.text(t('billing.hours'), 140, yPosition + 5, { align: 'right' });
      doc.text(t('common.total'), 165, yPosition + 5, { align: 'right' });
      yPosition += 8;

      // Summary table rows
      const items = typeof bill.items === "string" ? JSON.parse(bill.items) : bill.items;
      items.forEach((item: any, index: number) => {
        doc.setFillColor(index % 2 === 0 ? 255 : 250, index % 2 === 0 ? 255 : 250, index % 2 === 0 ? 255 : 250);
        doc.rect(20, yPosition, 170, 7, 'F');
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        
        doc.text(item.position || t('billing.service'), 25, yPosition + 4);
        doc.text(item.description.length > 20 ? item.description.substring(0, 17) + '...' : item.description, 65, yPosition + 4);
        doc.text(Number(item.unit_price).toLocaleString(), 110, yPosition + 4, { align: 'right' });
        doc.text(item.quantity.toString(), 140, yPosition + 4, { align: 'right' });
        doc.text(Number(item.total).toLocaleString(), 165, yPosition + 4, { align: 'right' });
        yPosition += 7;
      });
      
      // Summary total row
      doc.setFillColor(255, 152, 0);
      doc.rect(20, yPosition, 170, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(t('billing.totalChargeableAmount'), 25, yPosition + 5);
      const totalHours = items.reduce((sum: number, item: any) => sum + Number(item.quantity), 0);
      doc.text(totalHours.toString(), 140, yPosition + 5, { align: 'right' });
      doc.text(Number(bill.total).toLocaleString(), 165, yPosition + 5, { align: 'right' });
      yPosition += 15;
      
      // Description Section
      doc.setTextColor(25, 118, 210);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(t('common.description'), 20, yPosition);
      yPosition += 10;

      // Description table header
      doc.setFillColor(245, 245, 245);
      doc.rect(20, yPosition, 170, 8, 'F');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(t('common.date'), 25, yPosition + 5);
      doc.text(t('common.description'), 65, yPosition + 5);
      doc.text(t('billing.hours'), 140, yPosition + 5, { align: 'center' });
      doc.text(t('common.total'), 165, yPosition + 5, { align: 'right' });
      yPosition += 8;
      
      // Description table rows
      items.forEach((item: any, index: number) => {
        doc.setFillColor(index % 2 === 0 ? 255 : 250, index % 2 === 0 ? 255 : 250, index % 2 === 0 ? 255 : 250);
        doc.rect(20, yPosition, 170, 7, 'F');
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        
        doc.setTextColor(100, 100, 100);
        doc.text(bill.issued_date, 25, yPosition + 4);
        doc.setTextColor(0, 0, 0);
        doc.text(item.description.length > 35 ? item.description.substring(0, 32) + '...' : item.description, 65, yPosition + 4);
        doc.text(item.quantity.toString(), 140, yPosition + 4, { align: 'center' });
        doc.text(Number(item.total).toLocaleString(), 165, yPosition + 4, { align: 'right' });
        yPosition += 7;
      });
      
      // Description total row
      doc.setFillColor(156, 39, 176);
      doc.rect(20, yPosition, 170, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(t('billing.totalHoursWorked'), 25, yPosition + 5);
      doc.text(totalHours.toString(), 140, yPosition + 5, { align: 'center' });
      doc.text(Number(bill.total).toLocaleString(), 165, yPosition + 5, { align: 'right' });
      yPosition += 15;
      
      // Totals section (right aligned)
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      
      if (Number(bill.discount) > 0) {
        doc.text(`${t('billing.subtotal')}: ${Number(bill.subtotal).toLocaleString()} ${bill.currency}`, 190, yPosition, { align: 'right' });
        yPosition += 8;
        doc.setTextColor(244, 67, 54);
        doc.text(`${t('billing.discount')}: -${Number(bill.discount).toLocaleString()} ${bill.currency}`, 190, yPosition, { align: 'right' });
        yPosition += 8;
      } else {
        doc.text(`${t('billing.subtotal')}: ${Number(bill.subtotal).toLocaleString()} ${bill.currency}`, 190, yPosition, { align: 'right' });
        yPosition += 8;
      }

      if (Number(bill.tax_amount) > 0) {
        doc.setTextColor(0, 0, 0);
        doc.text(`${t('billing.taxWithRate', { rate: bill.tax_rate })}: ${Number(bill.tax_amount).toLocaleString()} ${bill.currency}`, 190, yPosition, { align: 'right' });
        yPosition += 8;
      }
      
      // Draw line above total
      doc.setDrawColor(221, 221, 221);
      doc.setLineWidth(0.5);
      doc.line(120, yPosition, 190, yPosition);
      yPosition += 6;
      
      // Total
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text(`${t('common.total')}: ${Number(bill.total).toLocaleString()} ${bill.currency}`, 190, yPosition, { align: 'right' });
      yPosition += 15;
      
      // Bank Details - Always include if available
      const bankDetails = getFirmSetting('bank_details');
      if (bankDetails) {
        // Check if we need to add a new page
        if (yPosition > 240) {
          doc.addPage();
          yPosition = 20;
        }
        
        yPosition += 10;
        doc.setTextColor(25, 118, 210);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text(`${t('billing.bankDetails')}:`, 20, yPosition);
        yPosition += 8;
        
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const bankLines = bankDetails.split('\n');
        bankLines.forEach((line) => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = 20;
          }
          doc.text(line, 20, yPosition);
          yPosition += 6;
        });
        yPosition += 10;
      }
      
      // Notes if available
      if (bill.notes) {
        // Check if we need to add a new page
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setTextColor(25, 118, 210);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text(`${t('common.notes')}:`, 20, yPosition);
        yPosition += 8;
        
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const noteLines = doc.splitTextToSize(bill.notes, 170);
        noteLines.forEach((line: string) => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = 20;
          }
          doc.text(line, 20, yPosition);
          yPosition += 6;
        });
      }
      
      // Footer - Fixed at bottom of page with reduced bottom margin
      const billFooter = getFirmSetting('bill_footer');
      if (billFooter) {
        const footerY = 285; // Reduced from 280 to 285 for smaller bottom margin
        doc.setTextColor(100, 100, 100);
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(8);
        
        // Center the footer
        const footerLines = billFooter.split('\n');
        footerLines.forEach((line, index) => {
          const textWidth = doc.getTextWidth(line);
          const x = (210 - textWidth) / 2; // Center horizontally
          doc.text(line, x, footerY + (index * 6));
        });
      }
      
      // Save the PDF
      doc.save(`Bill_${bill.bill_number}.pdf`);
      console.log('PDF saved successfully');
      
    } catch (error) {
      console.error("Failed to generate PDF:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: 0,
      case_id: 0,
      items: [{ description: "", quantity: 1, unit_price: 0, total: 0 }],
      subtotal: 0,
      tax_rate: 0,
      tax_amount: 0,
      discount: 0,
      total: 0,
      due_date: "",
      issued_date: new Date().toISOString().split("T")[0],
      currency: "USD",
    });
  };

  const openEditDialog = (bill: Bill) => {
    setEditingBill(bill);
    setFormData({
      customer_id: bill.customer_id,
      case_id: bill.case_id,
      items: bill.items,
      subtotal: bill.subtotal,
      tax_rate: bill.tax_rate,
      tax_amount: bill.tax_amount,
      discount: bill.discount,
      total: bill.total,
      due_date: bill.due_date,
      issued_date: bill.issued_date,
      currency: bill.currency,
    });
    setOpenDialog(true);
  };

  const columns: GridColDef[] = [
    { field: "bill_number", headerName: t("billing.billNumber"), width: 150 },
    {
      field: "customer_name",
      headerName: t("billing.client"),
      width: 200,
      valueGetter: (_value: unknown, row: any) => {
        const c = row.customer;
        if (!c) return "—";
        return c.company
          ? `${c.first_name} ${c.last_name} (${c.company})`
          : `${c.first_name} ${c.last_name}`;
      },
      renderCell: (params: GridRenderCellParams) => (
        <Stack spacing={0}>
          <Typography variant="body2" fontWeight={500}>
            {params.row.customer?.first_name} {params.row.customer?.last_name}
          </Typography>
          {params.row.customer?.company && (
            <Typography variant="caption" color="text.secondary">
              {params.row.customer.company}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      field: "case_subject",
      headerName: t("billing.case"),
      width: 200,
      valueGetter: (_value: unknown, row: any) => row.case_file?.subject ?? "—",
      renderCell: (params: GridRenderCellParams) => (
        <Stack spacing={0}>
          <Typography variant="body2" fontWeight={500}>
            {params.row.case_file?.subject ?? "—"}
          </Typography>
          {params.row.case_file?.type && (
            <Typography variant="caption" color="text.secondary">
              {params.row.case_file.type}
            </Typography>
          )}
        </Stack>
      ),
    },
    { field: "total", headerName: t("billing.amount"), width: 120 },
    {
      field: "status",
      headerName: t("billing.status"),
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          color={
            params.value === "paid"
              ? "success"
              : params.value === "sent"
                ? "warning"
                : "default"
          }
        />
      ),
    },
    { field: "issued_date", headerName: t("billing.issueDate"), width: 120 },
    { field: "due_date", headerName: t("billing.dueDate"), width: 120 },
    {
      field: "actions",
      headerName: t("common.actions"),
      width: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title={t("common.view")}>
            <IconButton
              size="small"
              onClick={() => openViewBillDialog(params.row as Bill)}
            >
              <ViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("common.edit")}>
            <IconButton
              size="small"
              onClick={() => openEditDialog(params.row as Bill)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("billing.downloadPDF")}>
            <IconButton
              size="small"
              onClick={() => handleDownloadPdf(params.row as Bill)}
            >
              <PdfIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("billing.sendInvoice")}>
            <IconButton
              size="small"
              onClick={() => handleSendBill(params.row.id)}
              disabled={params.row.status === "sent"}
            >
              <SendIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("billing.markAsPaid")}>
            <IconButton
              size="small"
              onClick={() => handleMarkAsPaid(params.row.id)}
              disabled={params.row.status === "paid"}
            >
              <PaidIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("common.delete")}>
            <IconButton
              size="small"
              onClick={() => handleDeleteBill(params.row.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <Typography>{t("billing.loadingBills")}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5">{t("billing.billsManagement")}</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              size="small"
              placeholder={t("billing.searchBillsPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
            >
              {t("common.search")}
            </Button>
            {/* <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingBill(null);
                resetForm();
                setOpenDialog(true);
              }}
            >
              New Bill
            </Button> */}
          </Stack>
        </Box>

        <Box sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={bills}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
          />
        </Box>
      </Card>

      {/* Create/Edit Bill Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingBill ? t("billing.editInvoice") : t("billing.createBill")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label={t("billing.customerId")}
              type="number"
              value={formData.customer_id}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  customer_id: parseInt(e.target.value),
                })
              }
              fullWidth
            />
            <TextField
              label={t("billing.caseId")}
              type="number"
              value={formData.case_id}
              onChange={(e) =>
                setFormData({ ...formData, case_id: parseInt(e.target.value) })
              }
              fullWidth
            />
            <TextField
              label={t("billing.totalAmount")}
              type="number"
              value={formData.total}
              onChange={(e) =>
                setFormData({ ...formData, total: parseFloat(e.target.value) })
              }
              fullWidth
            />
            <TextField
              label={t("billing.dueDate")}
              type="date"
              value={formData.due_date}
              onChange={(e) =>
                setFormData({ ...formData, due_date: e.target.value })
              }
              fullWidth
            />
            <TextField
              select
              label={t("billing.currency")}
              value={formData.currency}
              onChange={(e) =>
                setFormData({ ...formData, currency: e.target.value })
              }
              fullWidth
            >
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="GBP">GBP</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t("common.cancel")}</Button>
          <Button
            onClick={editingBill ? handleUpdateBill : handleCreateBill}
            variant="contained"
          >
            {editingBill ? t("common.update") : t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              {t("billing.billDetailsWithNumber", { number: viewingBill?.bill_number })}
            </Typography>
            <IconButton onClick={() => setOpenViewDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent 
          dividers 
          ref={billContentRef}
          sx={{ 
            flex: 1, 
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            pb: 0 // Remove bottom padding to make room for footer
          }}
        >
          {viewingBill && (
            <Box display="flex" flexDirection="column" gap={3}>
              {/* Firm Header */}
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                <Box flex={1}>
                  {firm?.logo_url && (
                    <Box
                      component="img"
                      src={firm.logo_url}
                      alt="Firm Logo"
                      sx={{ maxWidth: 175, maxHeight: 120, mb: 1 }}
                    />
                  )}
                  <Typography variant="h6" fontWeight={700} color="primary.main">
                    {firm?.name || t('billing.legalServices')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {firm?.address}, {firm?.city}, {firm?.country}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {firm?.email} | {firm?.phone}
                  </Typography>
                </Box>
                <Box textAlign="right">
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {t('billing.bill').toUpperCase()}
                  </Typography>
                  <Typography variant="h6" fontWeight={500}>
                    #{viewingBill.bill_number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {viewingBill.issued_date}
                  </Typography>
                </Box>
              </Box>

              {/* Header Info */}
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('billing.client')}
                  </Typography>
                  <Typography fontWeight={500}>
                    {viewingBill.customer?.first_name}{" "}
                    {viewingBill.customer?.last_name}
                  </Typography>
                  {viewingBill.customer?.company && (
                    <Typography variant="body2" color="text.secondary">
                      {viewingBill.customer.company}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('billing.case')}
                  </Typography>
                  <Typography fontWeight={500}>
                    {viewingBill.case_file?.subject ?? "—"}
                  </Typography>
                  {viewingBill.case_file?.type && (
                    <Typography variant="body2" color="text.secondary">
                      {viewingBill.case_file.type}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('billing.issueDate')}
                  </Typography>
                  <Typography>{viewingBill.issued_date}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('billing.dueDate')}
                  </Typography>
                  <Typography>{viewingBill.due_date}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('billing.status')}
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={viewingBill.status}
                      size="small"
                      color={
                        viewingBill.status === "paid"
                          ? "success"
                          : viewingBill.status === "sent"
                            ? "warning"
                            : "default"
                      }
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('billing.currency')}
                  </Typography>
                  <Typography>{viewingBill.currency}</Typography>
                </Box>
              </Box>

              {/* Summary Section */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{
                  borderBottom: 2,
                  borderColor: "primary.main",
                  pb: 1,
                  color: "primary.dark"
                }}>
                  {t('billing.summary')}
                </Typography>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  {/* Summary Table Header */}
                  <Box
                    display="grid"
                    gridTemplateColumns="1.5fr 1.5fr 1fr 1fr 1fr"
                    sx={{ 
                      bgcolor: "grey.100", 
                      px: 2, 
                      py: 1.5,
                      borderBottom: "1px solid",
                      borderColor: "divider"
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      {t('common.position')}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {t('billing.names')}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={600} textAlign="right">
                      {t('billing.rateWithCurrency', { currency: viewingBill.currency })}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={600} textAlign="right">
                      {t('billing.totalHours')}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={600} textAlign="right">
                      {t('billing.totalWithCurrency', { currency: viewingBill.currency })}
                    </Typography>
                  </Box>

                  {/* Summary Table Rows - Using actual bill items */}
                  {(typeof viewingBill.items === "string"
                    ? JSON.parse(viewingBill.items)
                    : viewingBill.items
                  ).map((item: any, index: number) => (
                    <Box
                      key={index}
                      display="grid"
                      gridTemplateColumns="1.5fr 1.5fr 1fr 1fr 1fr"
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderTop: index > 0 ? "1px solid" : "none",
                        borderColor: "divider",
                        bgcolor: index % 2 === 0 ? "white" : "grey.50"
                      }}
                    >
                      <Typography variant="body2">
                        {item.position || "Service"}
                      </Typography>
                      <Typography variant="body2">
                        {item.description}
                      </Typography>
                      <Typography variant="body2" textAlign="right">
                        {Number(item.unit_price).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" textAlign="right">
                        {item.quantity}
                      </Typography>
                      <Typography variant="body2" textAlign="right">
                        {Number(item.total).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}

                  {/* Total Row */}
                  <Box
                    display="grid"
                    gridTemplateColumns="1.5fr 1.5fr 1fr 1fr 1fr"
                    sx={{ 
                      px: 2, 
                      py: 2, 
                      borderTop: "2px solid",
                      borderColor: "divider",
                      fontWeight: "bold"
                    }}
                  >
                    <Typography gridColumn="span 3">{t("billing.totalChargeableAmount")}</Typography>
                    <Typography textAlign="right">
                      {(typeof viewingBill.items === "string"
                        ? JSON.parse(viewingBill.items).reduce((sum: number, item: any) => sum + Number(item.quantity), 0)
                        : viewingBill.items.reduce((sum: number, item: any) => sum + Number(item.quantity), 0)
                      )}
                    </Typography>
                    <Typography textAlign="right">
                      {Number(viewingBill.total).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Description Section */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ 
                  borderBottom: 2, 
                  borderColor: "primary.main", 
                  pb: 1,
                  color: "primary.dark"
                }}>
                  Description
                </Typography>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  {/* Description Table Header */}
                  <Box
                    display="grid"
                    gridTemplateColumns="1.5fr 4fr 1fr 1fr"
                    sx={{ 
                      bgcolor: "grey.100", 
                      px: 2, 
                      py: 1.5,
                      borderBottom: "1px solid",
                      borderColor: "divider"
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      Date
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Description
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={600} textAlign="center">
                      Hours
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={600} textAlign="right">
                      Total {viewingBill.currency}
                    </Typography>
                  </Box>

                  {/* Description Table Rows - Using actual bill items */}
                  {(typeof viewingBill.items === "string"
                    ? JSON.parse(viewingBill.items)
                    : viewingBill.items
                  ).map((item: any, index: number) => (
                    <Box
                      key={index}
                      display="grid"
                      gridTemplateColumns="1.5fr 4fr 1fr 1fr"
                      sx={{
                        px: 2,
                        py: 1,
                        borderTop: index > 0 ? "1px solid" : "none",
                        borderColor: "divider",
                        bgcolor: index % 2 === 0 ? "white" : "grey.50",
                        "&:hover": { bgcolor: "action.hover" }
                      }}
                    >
                      <Typography variant="body2" fontWeight={500} color="text.secondary">
                        {viewingBill.issued_date}
                      </Typography>
                      <Typography variant="body2">
                        {item.description}
                      </Typography>
                      <Typography variant="body2" textAlign="center">
                        {item.quantity}
                      </Typography>
                      <Typography variant="body2" textAlign="right">
                        {Number(item.total).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}

                  {/* Total Hours Row */}
                  <Box
                    display="grid"
                    gridTemplateColumns="1.5fr 4fr 1fr 1fr"
                    sx={{ 
                      px: 2, 
                      py: 1.5, 
                      borderTop: "2px solid",
                      borderColor: "divider",
                      fontWeight: "bold"
                    }}
                  >
                    <Typography gridColumn="span 2">{t("billing.totalHoursWorked")}</Typography>
                    <Typography textAlign="center">
                      {(typeof viewingBill.items === "string"
                        ? JSON.parse(viewingBill.items).reduce((sum: number, item: any) => sum + Number(item.quantity), 0)
                        : viewingBill.items.reduce((sum: number, item: any) => sum + Number(item.quantity), 0)
                      )}
                    </Typography>
                    <Typography textAlign="right">
                      {Number(viewingBill.total).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Original Totals Section */}
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-end"
                gap={0.5}
              >
                <Box display="flex" gap={4}>
                  <Typography color="text.secondary">{t("billing.subtotal")}</Typography>
                  <Typography>
                    {Number(viewingBill.subtotal).toLocaleString()}{" "}
                    {viewingBill.currency}
                  </Typography>
                </Box>
                {Number(viewingBill.discount) > 0 && (
                  <Box display="flex" gap={4}>
                    <Typography color="text.secondary">{t("billing.discount")}</Typography>
                    <Typography color="error.main">
                      -{Number(viewingBill.discount).toLocaleString()}{" "}
                      {viewingBill.currency}
                    </Typography>
                  </Box>
                )}
                {Number(viewingBill.tax_amount) > 0 && (
                  <Box display="flex" gap={4}>
                    <Typography color="text.secondary">
                      {t("billing.taxWithRate", { rate: viewingBill.tax_rate })}
                    </Typography>
                    <Typography>
                      {Number(viewingBill.tax_amount).toLocaleString()}{" "}
                      {viewingBill.currency}
                    </Typography>
                  </Box>
                )}
                <Box
                  display="flex"
                  gap={4}
                  sx={{
                    borderTop: "2px solid",
                    borderColor: "divider",
                    pt: 1,
                    mt: 0.5,
                  }}
                >
                  <Typography fontWeight={700}>{t("common.total")}</Typography>
                  <Typography fontWeight={700}>
                    {Number(viewingBill.total).toLocaleString()}{" "}
                    {viewingBill.currency}
                  </Typography>
                </Box>
              </Box>

              {/* Notes */}
              {viewingBill.notes && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Notes
                  </Typography>
                  <Typography variant="body2">{viewingBill.notes}</Typography>
                </Box>
              )}

              {/* Bank Details */}
              {getFirmSetting('bank_details') && (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ 
                    borderBottom: 2, 
                    borderColor: "primary.main", 
                    pb: 1,
                    color: "primary.dark"
                  }}>
                    Bank Details
                  </Typography>
                  <Typography variant="body2" whiteSpace="pre-line">
                    {getFirmSetting('bank_details')}
                  </Typography>
                </Box>
              )}

              {/* Spacer to push footer to bottom */}
              <Box sx={{ flexGrow: 1 }} />
            </Box>
          )}
        </DialogContent>

        {/* Fixed Footer */}
        {getFirmSetting('bill_footer') && (
          <Box
            sx={{
              px: 3,
              py: 2,
              bgcolor: 'grey.50',
              borderTop: '1px solid',
              borderColor: 'divider',
              textAlign: 'center'
            }}
          >
            <Typography variant="body2" color="text.secondary" whiteSpace="pre-line">
              {getFirmSetting('bill_footer')}
            </Typography>
          </Box>
        )}

        <DialogActions>
          <Button onClick={handlePrintBill} startIcon={<PdfIcon />}>
            {t("billing.printBill")}
          </Button>
          <Button onClick={() => setOpenViewDialog(false)}>{t("common.close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Bills;
